import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import logoImg from "../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   PALETTE — same HEART tokens HomeCentral uses
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  dark: "#1B0E06",
  textMuted: "#7A6653",
  red: "#C8202E",
  redDark: "#9E1521",
  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",
  border: "#EEDFC4",
  divider: "#F3E6CE",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  @keyframes csSpin { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
  @keyframes csShimmer { 0%{background-position:0% 50%;} 100%{background-position:200% 50%;} }
  @keyframes csBob { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-14px);} }
  @keyframes csWiggle { 0%,100%{transform:rotate(-2deg);} 50%{transform:rotate(2deg);} }

  .cs-scope { font-family: 'DM Sans', sans-serif; background: ${HEART.bg}; color: ${HEART.dark}; }

  /* 3D extruded type — layered text-shadow, same trick as the intro showcase */
  .cs-3d {
    font-family: 'Sora', sans-serif; font-weight: 900; letter-spacing: -0.03em;
    color: ${HEART.dark};
    text-shadow:
      0 1px 0 #fff,
      0 2px 0 ${HEART.divider},
      0 3px 0 ${HEART.border},
      0 4px 0 ${HEART.goldSoft},
      0 5px 0 ${HEART.gold},
      0 6px 0 ${HEART.goldDark},
      0 8px 14px rgba(27,14,6,0.28);
  }
  .cs-3d-red {
    font-family: 'Sora', sans-serif; font-weight: 900; letter-spacing: -0.03em;
    color: ${HEART.red};
    text-shadow:
      0 1px 0 #fff,
      0 2px 0 ${HEART.border},
      0 3px 0 ${HEART.goldSoft},
      0 4px 0 ${HEART.gold},
      0 5px 0 ${HEART.goldDark},
      0 7px 0 ${HEART.redDark},
      0 10px 18px rgba(200,32,46,0.32);
  }

  .cs-signup {
    display: inline-flex; align-items: center; gap: 8px;
    background: ${HEART.red}; color: #fff;
    border: 3px solid ${HEART.dark}; border-radius: 14px;
    padding: 12px 24px;
    font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 800;
    cursor: pointer; transition: background .2s, transform .15s; white-space: nowrap;
  }
  .cs-signup:hover { background: ${HEART.redDark}; transform: translateY(-2px); }

  .cs-logo-wiggle { animation: csWiggle 2.6s ease-in-out infinite; transform-origin: center; }

  @media (max-width: 768px) {
    .cs-brand   { font-size: 20px !important; }
    .cs-head    { font-size: 44px !important; }
    .cs-sub     { font-size: 15px !important; }
    .cs-logo    { width: 150px !important; height: 150px !important; }
    .cs-signup  { padding: 10px 18px !important; font-size: 13px !important; }
  }

  @media (prefers-reduced-motion: reduce) { .cs-scope * { animation: none !important; } }
`;

/* ═══════════════════════════════════════════════════════════
   WATERMARK — tiled logo + wordmark, same as IntroShowcase
═══════════════════════════════════════════════════════════ */
const Watermark: React.FC = () => (
  <div style={{
    position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden",
    display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows: "repeat(6, 1fr)",
  }}>
    {Array.from({ length: 36 }).map((_, i) => (
      <div key={i} style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        transform: i % 2 === 0 ? "rotate(-18deg)" : "rotate(-18deg) translateY(12px)",
        opacity: 0.05,
      }}>
        <img src={logoImg} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover" }} />
        <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, fontStyle: "italic", color: HEART.dark, whiteSpace: "nowrap" }}>
          Heartlas
        </span>
      </div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   COMING SOON
═══════════════════════════════════════════════════════════ */
const ComingSoon: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="cs-scope" style={{
      position: "relative", minHeight: "100vh", width: "100%", overflow: "hidden",
      display: "flex", flexDirection: "column",
    }}>
      <style>{CSS}</style>

      {/* tiled watermark */}
      <Watermark />

      {/* warm shimmer wash */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "linear-gradient(100deg, transparent 20%, rgba(200,32,46,0.09) 35%, rgba(242,169,62,0.11) 50%, rgba(212,144,15,0.09) 65%, transparent 80%)",
        backgroundSize: "300% 300%", animation: "csShimmer 3.6s ease-in-out infinite",
      }} />

      {/* ── TOP BAR ── */}
      <div style={{
        position: "relative", zIndex: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "22px 28px", gap: 16,
      }}>
        {/* top-left — 3D HEARTLAS */}
        <motion.span
          initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
          className="cs-3d cs-brand"
          style={{ fontSize: 26, lineHeight: 1 }}
        >
          HEARTLAS
        </motion.span>

        {/* top-right — Sign Up */}
        <motion.button
          initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
          className="cs-signup"
          onClick={() => navigate("/auth")}
        >
          Sign Up <ArrowRight size={14} />
        </motion.button>
      </div>

      {/* ── CENTER ── */}
      <div style={{
        position: "relative", zIndex: 1, flex: 1,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        textAlign: "center", padding: "20px 24px 70px",
      }}>
        <motion.img
          src={logoImg}
          alt="Heartlas"
          className="cs-logo cs-logo-wiggle"
          initial={{ opacity: 0, scale: 0.86, y: 18 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{
            width: 220, height: 220, borderRadius: 48, objectFit: "cover",
            marginBottom: 40, boxShadow: "0 22px 60px rgba(27,14,6,0.22)",
          }}
        />

        <motion.h1
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.15 }}
          className="cs-3d-red cs-head"
          style={{ fontSize: 68, lineHeight: 1.05, marginBottom: 18, maxWidth: 900 }}
        >
          Cool Stuff<br />Coming Soon
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
          className="cs-sub"
          style={{ fontSize: 17, fontWeight: 600, color: HEART.textMuted, maxWidth: 480, lineHeight: 1.7 }}
        >
          We're putting the finishing touches on something worth the wait.
        </motion.p>
      </div>
    </div>
  );
};

export default ComingSoon;
